import React, { useState, useEffect, useRef, useCallback } from 'react';
import { defaultProductExtendedMetadata, ProductExtendedMetadata } from '../../../types/product';
import {
  ProductInfoSection,
  PricingOfferSection,
  TargetSection,
  BenefitsSection,
  PositioningSection,
  AppealSection
} from '../../../components/product/sections';
import BrandGuidelineSection from '../../../components/knowledge/sections/BrandGuidelineSection';
import CompetitorAutoSection from '../../../components/knowledge/sections/CompetitorAutoSection';
import { BrandGuideline, FetchProgress } from '../../../types/product';
import {
  TaskyButton,
  TaskyInput,
  TaskySelect,
  TaskySpinner,
} from '../../../components/tasky';
import { cn } from '../../../lib/utils';
import productService from '../../../services/api/productService';
import categoryService from '../../../services/api/categoryService';
import tagService from '../../../services/api/tagService';
import { useToast } from '../../../contexts/ToastContext';

interface Category {
  id: number;
  name: string;
  slug?: string;
  description?: string;
}

interface Tag {
  id: number;
  name: string;
  slug: string;
}

interface ProductFormData {
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'discontinued';
  category_ids: number[];
  tag_ids: number[];
}

interface ProductCreateFormProps {
  product?: any; // 編集用の既存商品データ
  onSuccess?: () => void;
  onCancel?: () => void;
}

// 数値statusを文字列に変換する関数
const getStatusString = (status: number): string => {
  switch (status) {
    case 1: return 'active';
    case 2: return 'inactive';
    case 3: return 'discontinued';
    default: return 'active';
  }
};

const normalizeStatus = (status: number | string | undefined): ProductFormData['status'] => {
  if (typeof status === 'number') {
    return getStatusString(status) as ProductFormData['status'];
  }

  if (status === 'inactive' || status === 'discontinued') {
    return status;
  }

  return 'active';
};

const ProductCreateForm: React.FC<ProductCreateFormProps> = ({ product, onSuccess, onCancel }) => {
  const isEditing = !!product;
  const { showToast } = useToast();

  const [formData, setFormData] = useState<ProductFormData>({
    name: product?.name || '',
    description: product?.description || '',
    status: normalizeStatus(product?.status),
    category_ids: product?.categories?.map((c: any) => c.id) || [],
    tag_ids: product?.tags?.map((t: any) => t.id) || []
  });

  const [extendedMetadata, setExtendedMetadata] = useState<ProductExtendedMetadata>(
    product?.extended_metadata || defaultProductExtendedMetadata
  );

  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [fetchProgress, setFetchProgress] = useState<FetchProgress | null>(
    product?.extended_metadata?.fetch_progress || null
  );
  const [competitors, setCompetitors] = useState<any[]>([]);
  const [competitorsLoading, setCompetitorsLoading] = useState(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const isFetching = product?.status === 'fetching' || product?.status === 4;

  const pollFetchProgress = useCallback(async () => {
    if (!product?.id) return;
    try {
      const data = await productService.getKnowledgeFetchProgress(product.id);
      setFetchProgress(data.fetch_progress);
      if (data.extended_metadata) {
        setExtendedMetadata(prev => ({ ...prev, ...data.extended_metadata }));
      }
      const allDone = data.fetch_progress &&
        Object.values(data.fetch_progress).every((s: any) => s === 'completed' || s === 'completed_with_warning' || s === 'failed');
      if (allDone && pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    } catch (err) {
      console.error('Fetch progress polling error:', err);
    }
  }, [product?.id]);

  useEffect(() => {
    if (isFetching && product?.id) {
      pollFetchProgress();
      pollIntervalRef.current = setInterval(pollFetchProgress, 5000);
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isFetching, product?.id, pollFetchProgress]);

  useEffect(() => {
    if (!product?.id) return;
    setCompetitorsLoading(true);
    productService.getKnowledgeCompetitors(product.id)
      .then(data => setCompetitors(data || []))
      .catch(() => setCompetitors([]))
      .finally(() => setCompetitorsLoading(false));
  }, [product?.id]);

  const handleBrandGuidelineUpdate = async (guideline: BrandGuideline) => {
    if (!product?.id) return;
    try {
      await productService.updateBrandGuideline(product.id, guideline);
      setExtendedMetadata(prev => ({ ...prev, brand_guideline: guideline }));
    } catch (err) {
      console.error('Brand guideline update error:', err);
    }
  };

  // カテゴリとタグのデータを取得
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [categoriesData, tagsData] = await Promise.all([
          categoryService.getProductCategories(),
          tagService.getAll()
        ]);

        setCategories(categoriesData);
        setTags(tagsData.data || []);
      } catch (error) {
        console.error('データの取得に失敗しました:', error);
        showToast('データの取得に失敗しました', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'status' ? normalizeStatus(value) : value
    }));
  };

  const handleCategoryChange = (categoryId: number) => {
    setFormData(prev => ({
      ...prev,
      category_ids: prev.category_ids.includes(categoryId)
        ? prev.category_ids.filter(id => id !== categoryId)
        : [...prev.category_ids, categoryId]
    }));
  };

  const handleTagChange = (tagId: number) => {
    setFormData(prev => ({
      ...prev,
      tag_ids: prev.tag_ids.includes(tagId)
        ? prev.tag_ids.filter(id => id !== tagId)
        : [...prev.tag_ids, tagId]
    }));
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      showToast('カテゴリ名を入力してください', 'error');
      return;
    }

    setIsAddingCategory(true);
    try {
      const newCategory = await categoryService.createProductCategory({
        name: newCategoryName.trim(),
        description: ''
      });

      setCategories(prev => [...prev, newCategory]);
      setFormData(prev => ({
        ...prev,
        category_ids: [...prev.category_ids, newCategory.id]
      }));
      setNewCategoryName('');
      showToast('カテゴリが追加されました', 'success');
    } catch (error) {
      console.error('カテゴリ追加エラー:', error);
      showToast('カテゴリの追加に失敗しました', 'error');
    } finally {
      setIsAddingCategory(false);
    }
  };

  const handleAddTag = async () => {
    if (!newTagName.trim()) {
      showToast('タグ名を入力してください', 'error');
      return;
    }

    setIsAddingTag(true);
    try {
      const newTag = await tagService.create({ name: newTagName.trim() });

      setTags(prev => [...prev, newTag.data]);
      setFormData(prev => ({
        ...prev,
        tag_ids: [...prev.tag_ids, newTag.data.id]
      }));
      setNewTagName('');
      showToast('タグが追加されました', 'success');
    } catch (error) {
      console.error('タグ追加エラー:', error);
      showToast('タグの追加に失敗しました', 'error');
    } finally {
      setIsAddingTag(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showToast('商品名は必須です', 'error');
      return;
    }

    // 説明フィールドは一時的に非必須（バリデーションを外している）

    if (!formData.status) {
      showToast('ステータスは必須です', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      // extended_metadataを含めた送信データを作成
      const submitData = {
        ...formData,
        extended_metadata: extendedMetadata
      };

      if (isEditing) {
        await productService.updateKnowledgeProduct(product.id, submitData);
      } else {
        await productService.createKnowledgeProduct(submitData);
      }

      showToast(isEditing ? '商品を更新しました' : '商品を登録しました');

      if (!isEditing) {
        setFormData({
          name: '',
          description: '',
          status: 'active',
          category_ids: [],
          tag_ids: []
        });
        setExtendedMetadata(defaultProductExtendedMetadata);
      }

      onSuccess?.();
    } catch (error) {
      console.error('商品作成エラー:', error);
      showToast('商品の作成に失敗しました', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 h-[200px] text-base text-gray-500">
        <TaskySpinner size="md" />
        データを読み込み中...
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto text-dark">
      <h1 className="text-xl font-semibold text-dark mb-6">{isEditing ? '商品編集' : '新規商品追加'}</h1>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4 pb-24">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <label htmlFor="name" className="text-sm font-medium text-dark">商品名</label>
            <span className="inline-block px-2 py-0.5 bg-destructive text-white text-[11px] font-semibold rounded-tasky-xs">必須</span>
          </div>
          <TaskyInput
            type="text"
            id="name"
            name="name"
            className="bg-warm-surface border-none text-sm"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="商品名を入力してください"
            required
          />
        </div>

        {/* 説明フィールド - 一時的に非表示 */}
        {/* <FormGroup>
          <LabelRow>
            <Label htmlFor="description">説明</Label>
            <RequiredBadge>必須</RequiredBadge>
          </LabelRow>
          <TextArea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="商品の説明を入力してください"
            rows={4}
            required
          />
        </FormGroup> */}

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <label htmlFor="status" className="text-sm font-medium text-dark">ステータス</label>
            <span className="inline-block px-2 py-0.5 bg-destructive text-white text-[11px] font-semibold rounded-tasky-xs">必須</span>
          </div>
          <TaskySelect
            id="status"
            name="status"
            className="bg-warm-surface border-none text-sm"
            value={formData.status}
            onChange={handleInputChange}
            required
          >
            <option value="active">アクティブ</option>
            <option value="inactive">非アクティブ</option>
            <option value="discontinued">廃止予定</option>
          </TaskySelect>
        </div>

        {/* カテゴリフィールド - 一時的に非表示 */}
        {/* <FormGroup>
          <Label>カテゴリ</Label>
          <AddItemContainer>
            <AddItemInput
              type="text"
              placeholder="新しいカテゴリを追加"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCategory())}
            />
            <AddItemButton
              type="button"
              onClick={handleAddCategory}
              disabled={isAddingCategory}
            >
              {isAddingCategory ? '追加中...' : '追加'}
            </AddItemButton>
          </AddItemContainer>
          <CheckboxGrid>
            {categories.map(category => (
              <CheckboxItem key={category.id}>
                <Checkbox
                  type="checkbox"
                  id={`category-${category.id}`}
                  checked={formData.category_ids.includes(category.id)}
                  onChange={() => handleCategoryChange(category.id)}
                />
                <CheckboxLabel htmlFor={`category-${category.id}`}>
                  {category.name}
                </CheckboxLabel>
              </CheckboxItem>
            ))}
          </CheckboxGrid>
        </FormGroup> */}

        {/* タグフィールド - 一時的に非表示 */}
        {/* <FormGroup>
          <Label>タグ</Label>
          <AddItemContainer>
            <AddItemInput
              type="text"
              placeholder="新しいタグを追加"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
            />
            <AddItemButton
              type="button"
              onClick={handleAddTag}
              disabled={isAddingTag}
            >
              {isAddingTag ? '追加中...' : '追加'}
            </AddItemButton>
          </AddItemContainer>
          <TagContainer>
            {tags.map(tag => (
              <TagItem key={tag.id}>
                <TagCheckbox
                  type="checkbox"
                  id={`tag-${tag.id}`}
                  checked={formData.tag_ids.includes(tag.id)}
                  onChange={() => handleTagChange(tag.id)}
                />
                <TagLabel
                  htmlFor={`tag-${tag.id}`}
                  isSelected={formData.tag_ids.includes(tag.id)}
                >
                  {tag.name}
                </TagLabel>
              </TagItem>
            ))}
          </TagContainer>
        </FormGroup> */}

        {/* Fetch Progress Indicator */}
        {fetchProgress && isFetching && (
          <div className="p-4 rounded-tasky-sm bg-primary/10 mb-4">
            <div className="text-sm font-semibold text-primary mb-3">URL情報取得中...</div>
            <div className="grid grid-cols-2 gap-2">
              {([
                ['basic_info', '基本情報'],
                ['brand_guideline', 'ブランドガイドライン'],
                ['images', '画像'],
              ] as const).map(([key, label]) => (
                <div key={key} className="flex items-center gap-2 text-[13px] text-gray-600">
                  <span
                    className={cn(
                      'w-2 h-2 rounded-full flex-shrink-0',
                      fetchProgress[key] === 'completed'
                        ? 'bg-success'
                        : fetchProgress[key] === 'failed'
                          ? 'bg-destructive'
                          : fetchProgress[key] === 'completed_with_warning'
                            ? 'bg-warning'
                            : 'bg-warning animate-pulse',
                    )}
                  />
                  <span>{label}</span>
                  <span
                    className={cn(
                      'ml-auto text-xs',
                      fetchProgress[key] === 'completed'
                        ? 'text-success'
                        : fetchProgress[key] === 'failed'
                          ? 'text-destructive'
                          : 'text-warning',
                    )}
                  >
                    {fetchProgress[key] === 'completed' ? '完了' : fetchProgress[key] === 'completed_with_warning' ? '一部不足' : fetchProgress[key] === 'failed' ? '失敗' : '取得中...'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Extended Metadata Sections */}
        <div className="mt-8 pt-8 border-t border-warm-border">
          <h2 className="text-base font-semibold text-dark mb-6">拡張マーケティング情報</h2>

          <ProductInfoSection
            value={extendedMetadata.product || {}}
            onChange={(product) => setExtendedMetadata({ ...extendedMetadata, product })}
          />

          {/* Brand Guideline Section */}
          <hr className="border-0 border-t border-warm-border my-6" />
          <BrandGuidelineSection
            guideline={extendedMetadata.brand_guideline}
            onUpdate={product?.id ? handleBrandGuidelineUpdate : undefined}
            readOnly={!isEditing}
          />

          <PricingOfferSection
            value={extendedMetadata.pricing_offer || {}}
            onChange={(pricing_offer) => setExtendedMetadata({ ...extendedMetadata, pricing_offer })}
          />

          <TargetSection
            value={extendedMetadata.target || {}}
            onChange={(target) => setExtendedMetadata({ ...extendedMetadata, target })}
          />

          <BenefitsSection
            value={extendedMetadata.benefits || {}}
            onChange={(benefits) => setExtendedMetadata({ ...extendedMetadata, benefits })}
          />

          <PositioningSection
            value={extendedMetadata.positioning || {}}
            onChange={(positioning) => setExtendedMetadata({ ...extendedMetadata, positioning })}
          />

          <AppealSection
            value={extendedMetadata.appeal || {}}
            onChange={(appeal) => setExtendedMetadata({ ...extendedMetadata, appeal })}
          />

          {/* Competitor Auto Section */}
          {isEditing && (
            <>
              <hr className="border-0 border-t border-warm-border my-6" />
              <CompetitorAutoSection
                competitors={competitors}
                loading={competitorsLoading}
              />
            </>
          )}
        </div>

        <div className="sticky bottom-0 z-10 flex items-center justify-end gap-3 mt-6 py-4 bg-white border-t border-warm-border">
          {onCancel && (
            <TaskyButton
              type="button"
              variant="outline"
              size="sm"
              className="px-6"
              onClick={onCancel}
            >
              キャンセル
            </TaskyButton>
          )}
          <TaskyButton
            type="submit"
            size="sm"
            className="px-6"
            disabled={isSubmitting}
          >
            {isSubmitting ? (isEditing ? '更新中...' : '作成中...') : (isEditing ? '商品を更新' : '商品を作成')}
          </TaskyButton>
        </div>
      </form>
    </div>
  );
};

export default ProductCreateForm;
